import ConfirmationModal from "components/ConfirmationModal";
import DocumentViewerModal from "components/DocumentViewer";
import FileActions from "components/FileDownloadButtons";
import { useToast } from "contexts/ToastContext";
import { useTranslation } from "contexts/i18nContext";
import { useHasRole } from "contexts/AuthContext";
import EditAccusedSummonModal from "pages/advocateComplaint/fragments/EditAccusedSummonModal";
import { useEffect, useState } from "react";
import {
  editAccusedSummon,
  editComplaint,
  fetchAdminUnitDetail,
} from "services/advocateComplaintService";
import {
  AccusedSummon,
  AdminUnit,
  AdvocateComplaints,
  Litigation,
} from "types";
import {
  ACCUSED_COMPLAINT_STATUS,
  ADVOCATE_ACCUSED_COMPLAINT_STATUS,
} from "utils/constantValues";
import { DateView } from "utils/helper";
import { ADVOCATE_ROLES, ROLES } from "utils/roles";
import AccusedSummonConfirmationModal from "./AccusedSummonConfirmationModal";
import AddAccusedSummonRegistrationModal from "./AddAccusedSummonRegistrationModal";
import AddSummonResponse from "./AddSummonResponse";
import { useAuth } from "contexts/AuthContext";

interface SummonProps {
  summonData: AccusedSummon;
  compliantData: AdvocateComplaints;
  onSummonRefresh: any;
  appealLitigationData?: Litigation[];
  onComplaintsRefresh: any;
  isAppealToGeneralCouncil?: boolean;
}

export default function AccusedSummonCard({
  summonData,
  compliantData,
  onSummonRefresh,
  appealLitigationData,
  onComplaintsRefresh,
  isAppealToGeneralCouncil = false,
}: SummonProps) {
  const { user } = useAuth();
  const { t } = useTranslation();

  const canCoordinatorConfirmSummon = useHasRole([
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
  ]);
  const canAccusedConfirmSummon = useHasRole([
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
  ]);
  const canUpdateHearingDate = useHasRole([
    ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canCreateAccusedSummon = useHasRole([
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
  ]);
  const canCreateAccusedSummonForZonal = useHasRole([
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canRegisterAccusedSummon = useHasRole([
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
    ROLES.INSPECTION_REGISTRAR,
  ]);
  const canSendAccusedSummonToCouncil = useHasRole([
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
    ROLES.INSPECTION_REGISTRAR,
  ]);
  const canSendAccusedSummonToRegistrar = useHasRole([
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
  ]);
  // const canApproveAccusedSummon = useHasRole([
  //   ADVOCATE_ROLES.INSPECTION_COORDINATOR,
  //   ADVOCATE_ROLES.INSPECTION_DIRECTOR,
  // ]);

  const { showToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [issSendToRegistrar, setIsSendToRegistrar] = useState(false);
  const [isSendToCouncilModal, setIsSendToCouncilModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState<boolean>(false);
  const [coordinatorConfirmationModal, setCoordinatorConfirmationModal] =
    useState<boolean>(false);
  const [createSummonModal, setCreateSummonModal] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [registerModal, setRegisterModal] = useState<boolean>(false);
  const [isEditingHearingDate, setIsEditingHearingDate] = useState(false);
  const [hearingDateDraft, setHearingDateDraft] = useState("");
  const [isUpdatingHearingDate, setIsUpdatingHearingDate] = useState(false);

  const [adminUnitData, setAdminUnitData] = useState<AdminUnit>();

  const [canReassignProsecutor, setCanReassignProsecutor] = useState(false);
  useEffect(() => {
    if (appealLitigationData) {
      setCanReassignProsecutor(
        appealLitigationData?.find((a) =>
          a?.verdict_documents?.some(
            (v: { document_type: string }) =>
              v?.document_type === "council_decision_by_pro",
          ),
        )
          ? false
          : true,
      );
    }
  }, [appealLitigationData]);

  useEffect(() => {
    const loadAdminUnitDetail = async () => {
      try {
        const adminUnitDetail = await fetchAdminUnitDetail(
          user?.admin_unit,
          user?.access,
        );
        setAdminUnitData(adminUnitDetail);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    };
    loadAdminUnitDetail();
  }, [user]);

  const toDateInputValue = (value?: string) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().slice(0, 10);
  };

  const startHearingDateEdit = () => {
    setHearingDateDraft(toDateInputValue(summonData?.hearing_date));
    setIsEditingHearingDate(true);
  };

  const cancelHearingDateEdit = () => {
    setIsEditingHearingDate(false);
    setHearingDateDraft("");
  };

  const handleUpdateHearingDate = async () => {
    if (!summonData?.id) {
      showToast(t("summonNotFound"), "error");
      return;
    }

    const currentDate = toDateInputValue(summonData?.hearing_date);
    if (!hearingDateDraft || hearingDateDraft === currentDate) {
      showToast("No date change detected", "error");
      return;
    }

    setIsUpdatingHearingDate(true);
    try {
      await editAccusedSummon(
        {
          hearing_date: hearingDateDraft,
        } as any,
        summonData.id,
        false,
      );

      await Promise.resolve(onSummonRefresh?.());
      cancelHearingDateEdit();
      showToast("Hearing date updated", "success");
    } catch (err: any) {
      showToast(t("submissionError"), "error");
    } finally {
      setIsUpdatingHearingDate(false);
    }
  };

  const handleApproveClick = async () => {
    if (!summonData) {
      showToast(t("summonNotFound"), "error");
      return;
    }
    setLoading(true);

    const isAccusedConfirmed = true;
    const isCoordinatorConfirmed = true;

    const data = new FormData();
    data.append("accused_confirmed", isAccusedConfirmed.toString());
    data.append("coordinator_confirmed", isCoordinatorConfirmed.toString());
    data.append("status", ACCUSED_COMPLAINT_STATUS.APPROVED);
    data.append(
      "admin_unit_id",
      adminUnitData?.parent_unit
        ? adminUnitData?.parent_unit
        : compliantData?.admin_unit_id || "",
    );
    data.append("org_id", compliantData?.org_id || "");
    data.append("accused_advocate_name", compliantData?.accused_name || "");
    data.append(
      "user_id_accused_advocate",
      compliantData?.userid_accused_name || "",
    );
    data.append(
      "accused_license_number",
      compliantData?.accused_license_number || "",
    );

    const summon_id = summonData?.id || "";
    const isFormData = true;

    try {
      const resData = await editAccusedSummon(data, summon_id, isFormData);

      if (resData) {
        editComplaint({
          ...compliantData,
          admin_unit_id: adminUnitData?.parent_unit
            ? adminUnitData?.parent_unit
            : compliantData?.admin_unit_id || "",
        });
      }

      onSummonRefresh();
      setShowConfirmationModal(false);
      showToast(t("successfullyConfirmed"), "success");
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSendToCouncilConfirm = async () => {
    if (!isSendToCouncilModal) return;

    if (!summonData) {
      showToast(t("summonNotFound"), "error");
      return;
    }
    setLoading(true);

    const data = new FormData();

    data.append(
      "status",
      ADVOCATE_ACCUSED_COMPLAINT_STATUS.FORWARDED_TO_COUNCIL_OFFICE,
    );
    data.append("admin_unit_id", compliantData?.admin_unit_id || "");
    data.append("org_id", compliantData?.org_id || "");
    data.append("accused_advocate_name", compliantData?.accused_name || "");
    data.append(
      "user_id_accused_advocate",
      compliantData?.userid_accused_name || "",
    );
    data.append(
      "accused_license_number",
      compliantData?.accused_license_number || "",
    );

    const summon_id = summonData?.id || "";
    const isFormData = true;

    try {
      await editAccusedSummon(data, summon_id, isFormData);

      onSummonRefresh();
      setIsSendToCouncilModal(false);
      showToast(t("successfullyConfirmed"), "success");
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSendToRegistrar = async () => {
    if (!issSendToRegistrar) return;

    if (!summonData) {
      showToast(t("summonNotFound"), "error");
      return;
    }
    setLoading(true);

    const data = new FormData();

    data.append(
      "status",
      ADVOCATE_ACCUSED_COMPLAINT_STATUS.FORWARDED_TO_REGISTRAR,
    );
    data.append("admin_unit_id", compliantData?.admin_unit_id || "");
    data.append("org_id", compliantData?.org_id || "");
    data.append("accused_advocate_name", compliantData?.accused_name || "");
    data.append(
      "user_id_accused_advocate",
      compliantData?.userid_accused_name || "",
    );
    data.append(
      "accused_license_number",
      compliantData?.accused_license_number || "",
    );

    const summon_id = summonData?.id || "";
    const isFormData = true;

    try {
      await editAccusedSummon(data, summon_id, isFormData);

      onSummonRefresh();
      setIsSendToRegistrar(false);
      showToast(t("successfullyConfirmed"), "success");
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  if (!summonData) return null;

  return (
    <div className="mb-8">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-800 border-l-4 border-red-600 pl-3 py-1">
          {t("accusedSummon")}
        </h2>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-4">
        <div className="p-5">
          {/* Assigned Prosecutor */}
          {summonData?.assiegned_prosecutor && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                {t("assignedProsecutor")}
              </h3>
              <div className="relative border border-gray-400 bg-white rounded-md p-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img
                      src={require("../../../images/avatar.png")}
                      alt="avatar"
                      className="w-8 h-8 rounded-full"
                    />
                    <div>
                      <p className="font-medium text-sm">
                        {summonData?.assiegned_prosecutor || "-"}
                      </p>
                      <p className="text-xs text-gray-500">{t("assignedTo")}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Registration Details */}
          {(summonData?.registration_date ||
            summonData?.registration_number) && (
            <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {summonData?.registration_date && (
                  <div>
                    <p className="text-xs text-gray-500">
                      {t("registeredDate")}
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {summonData.registration_date}
                    </p>
                  </div>
                )}
                {summonData?.registration_number && (
                  <div>
                    <p className="text-xs text-gray-500">
                      {t("registrationNumber")}
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {summonData.registration_number}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                {t("hearingInformation")}
              </h4>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">{t("hearingDate")}</p>
                    <div className="flex items-center gap-3">
                      <p className="text-sm font-medium text-gray-900">
                        {summonData?.hearing_date
                          ? DateView(summonData.hearing_date)
                          : "-"}
                      </p>
                      {canUpdateHearingDate && (
                        <button
                          type="button"
                          onClick={startHearingDateEdit}
                          className="text-xs font-medium text-red-700 hover:text-red-800 underline"
                        >
                          {t("updateDate")}
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">
                      {t("addressInformation")}
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {summonData?.region ||
                      summonData?.zone ||
                      summonData?.woreda ||
                      summonData?.kebele
                        ? `R-${summonData?.region || ""}, Z-${summonData?.zone || ""}, W-${summonData?.woreda || ""}, K-${summonData?.kebele || ""}`
                        : "-"}
                    </p>
                  </div>
                </div>

                {isEditingHearingDate && (
                  <div className="mt-3 rounded-md border border-gray-200 bg-white p-3 space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {t("hearingDate")}
                      </label>
                      <input
                        type="date"
                        value={hearingDateDraft}
                        onChange={(e) => setHearingDateDraft(e.target.value)}
                        className="w-full md:w-64 rounded border border-gray-300 px-2 py-1.5 text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={isUpdatingHearingDate}
                        onClick={handleUpdateHearingDate}
                        className="rounded bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 disabled:opacity-60"
                      >
                        {isUpdatingHearingDate ? "Updating..." : "Save Date"}
                      </button>
                      <button
                        type="button"
                        disabled={isUpdatingHearingDate}
                        onClick={cancelHearingDateEdit}
                        className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                      >
                        {t("cancel")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Claim Summary */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                {t("claimSummary")}
              </h4>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {summonData?.claim_summary || t("noClaimSummaryAvailable")}
                </p>
              </div>
            </div>

            {/* Response Summary */}
            {summonData?.response_summary && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  {t("responseSummary")}
                </h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {summonData?.response_summary || "-"}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Files */}
          {summonData?.appeal_documents &&
            summonData.appeal_documents.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  {t("documents")}
                </h4>
                <DocumentViewerModal
                  documents={summonData.appeal_documents}
                  baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                  layout="list"
                />
              </div>
            )}

          {/* Response Document */}
          {summonData?.response && (
            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-red-800"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {summonData?.response_file_details?.file_name ||
                        t("responseDocument")}
                    </p>
                  </div>
                </div>
                <FileActions fileUrl={summonData?.response || ""} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex flex-wrap gap-3">
        {canSendAccusedSummonToCouncil &&
          summonData?.status === ADVOCATE_ACCUSED_COMPLAINT_STATUS.REVISION && (
            <button
              className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
              onClick={() => setIsSendToCouncilModal(true)}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                />
              </svg>
              {t("sendToCouncil")}
            </button>
          )}

        {(compliantData?.is_regional || isAppealToGeneralCouncil
          ? canCreateAccusedSummon
          : canCreateAccusedSummonForZonal) &&
          summonData?.status ===
            ADVOCATE_ACCUSED_COMPLAINT_STATUS.FORWARDED_TO_COUNCIL_OFFICE && (
            <button
              className="flex items-center gap-2 px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors"
              onClick={() => setCreateSummonModal(true)}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              {t("createSummon")}
            </button>
          )}

        {canCoordinatorConfirmSummon &&
          summonData?.status ===
            ADVOCATE_ACCUSED_COMPLAINT_STATUS.FORWARDED_TO_COORDINATOR &&
          !summonData?.coordinator_confirmed && (
            <button
              className="flex items-center gap-2 px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors"
              onClick={() => setCoordinatorConfirmationModal(true)}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              {t("assignProsecutor")}
            </button>
          )}

        {canCoordinatorConfirmSummon &&
          summonData?.coordinator_confirmed &&
          canReassignProsecutor && (
            <button
              className="flex items-center gap-2 px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors"
              onClick={() => setCoordinatorConfirmationModal(true)}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {t("reassignProsecutor")}
            </button>
          )}

        {canAccusedConfirmSummon &&
          summonData?.coordinator_confirmed &&
          !summonData?.accused_confirmed && (
            <button
              className="flex items-center gap-2 px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors"
              onClick={() => setShowResponseModal(true)}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              {t("addResponse")}
            </button>
          )}

        {canSendAccusedSummonToRegistrar &&
          summonData?.coordinator_confirmed &&
          summonData?.accused_confirmed &&
          summonData?.status ===
            ADVOCATE_ACCUSED_COMPLAINT_STATUS.ASSIGNED_TO_PROSECUTOR && (
            <button
              className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
              onClick={() => setIsSendToRegistrar(true)}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                />
              </svg>
              {t("sendToRegistrar")}
            </button>
          )}

        {canRegisterAccusedSummon &&
          summonData?.status ===
            ADVOCATE_ACCUSED_COMPLAINT_STATUS.FORWARDED_TO_REGISTRAR &&
          !summonData?.registration_number && (
            <button
              className="flex items-center gap-2 px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors"
              onClick={() => setRegisterModal(true)}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              {t("addRegistrationNo")}
            </button>
          )}
      </div>

      {/* {canApproveAccusedSummon &&
        summonData?.coordinator_confirmed &&
        summonData?.accused_confirmed &&
        summonData?.status !== ACCUSED_COMPLAINT_STATUS.APPROVED && (
          <div className="text-right">
            <button
              className="bg-black text-white px-14 py-2 rounded-md hover:bg-gray-800"
              type="submit"
              onClick={() => setShowConfirmationModal(true)}
            >
              Approve
            </button>
          </div>
        )} */}

      {summonData && compliantData && (
        <AccusedSummonConfirmationModal
          isOpen={coordinatorConfirmationModal}
          setIsOpen={setCoordinatorConfirmationModal}
          complaintData={compliantData}
          summonData={summonData}
          onRefresh={onSummonRefresh}
          onComplaintsRefresh={onComplaintsRefresh}
        />
      )}

      {summonData && compliantData && (
        <AddSummonResponse
          isOpen={showResponseModal}
          setIsOpen={setShowResponseModal}
          summonData={summonData}
          complaintData={compliantData}
          onRefresh={onSummonRefresh}
        />
      )}

      {summonData && compliantData && (
        <EditAccusedSummonModal
          isOpen={createSummonModal}
          setIsOpen={setCreateSummonModal}
          summonData={summonData}
          complaintData={compliantData}
          onRefresh={onSummonRefresh}
        />
      )}

      {summonData && compliantData && (
        <AddAccusedSummonRegistrationModal
          isOpen={registerModal}
          setIsOpen={setRegisterModal}
          summonData={summonData}
          complaintData={compliantData}
          onRefresh={onSummonRefresh}
        />
      )}

      <ConfirmationModal
        isOpen={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={handleApproveClick}
        isLoading={loading}
      />

      <ConfirmationModal
        isOpen={isSendToCouncilModal}
        onCancel={() => setIsSendToCouncilModal(false)}
        onConfirm={handleSendToCouncilConfirm}
        isLoading={loading}
      />

      <ConfirmationModal
        isOpen={issSendToRegistrar}
        onCancel={() => setIsSendToRegistrar(false)}
        onConfirm={handleSendToRegistrar}
        isLoading={loading}
      />
    </div>
  );
}
